import { browser } from "$app/environment";
import { goto } from "$app/navigation";
import { resolve } from "$app/paths";
import ErrorAlert from "$components/ErrorAlert.svelte";
import { m } from "$lib/paraglide/messages";
import { showAlert as _showAlert, type AlertOptions } from "@brave/leo/src/components/alert/alertCenter.svelte";
import { completePurchase, purchaseIfNecessary, recoverOrder, type Order } from "./payments-service";
import { getPriceKey, type Product } from "./products";
import { updatePaymentMethod } from "./subscription-service";
import { getRecoveryURL, isBrave } from "./utils";
import CopyText from "$components/CopyText.svelte";
import AwaitButton from "$components/AwaitButton.svelte";

const ALERT_DURATION = 6000;

export const TOO_MANY_RECOVERIES = ({ displayName }: Product) =>
  showAlert(
    {
      content: m.auth_trouble_message({ displayName }),
      type: "warning",
    },
    ALERT_DURATION
  );

export const DEVICE_LIMIT_REACHED = ({ displayName }: Product) =>
  showAlert(
    {
      content: m.device_limit_reached_message({ displayName }),
      type: "warning",
    },
    ALERT_DURATION
  );

export const FAILED_LOGIN = (error: any) =>
  showError(
    {
      content: m.auth_login_error(),
      error
    }
  );

export const UNSUPPORTED_BROWSER = (message?: string, name?: string, showDownload: boolean = true) => {
  showAlert({
    content: message || m.unsupported_browser_message({ name }),
    type: "warning",
    actions:
      showDownload && !isBrave()
        ? [
            {
              text: m.download_brave_link(),
              action: () => window.open("https://brave.com/download/now/", "_blank", "noopener=true"),
            },
          ]
        : undefined,
  });
};

export const ORDER_UNPAID = (order: Order) =>
  showAlert(
    {
      content: m.order_unpaid_message({ displayName: order.product.displayName, product: order.product.name }),
      type: "error",
      actions: [
        {
          text: m.complete_purchase_button(),
          action: () =>
            order.orderStatus === "pending"
              ? completePurchase(order)
              : purchaseIfNecessary(getPriceKey(order.currency, order.period)),
        },
      ],
    },
    ALERT_DURATION
  );

export const PAYMENT_OVERDUE = (order: Order) =>
  showAlert(
    {
      content: m.payment_overdue_message({ displayName: order.product.displayName }),
      type: "error",
      actions: [
        {
          text: m.early_weary_llama_roam(),
          action: () => updatePaymentMethod(order.subscriptionId),
        },
      ],
    },
    ALERT_DURATION
  );

export const NOT_PURCHASED = (order: Order) => {
  const uxMode: "mobile" | any = (browser && new URLSearchParams(location.search).get("ux")) ?? undefined;
  return showAlert(
    {
      content: m.gray_thick_stoat_seek({ product: order.product.name }),
      type: "error",
      actions: !uxMode
        ? [
            {
              text: m.browse_plans_button(),
              action: async (a) => {
                await goto(resolve('/plans/'));
                a.dismiss();
              },
            },
          ]
        : [],
    },
    ALERT_DURATION
  );
};

export const SUCCESS = (order: Order) => {
  const { product, productDomain, orderId } = order;

  let actions: Array<any>;
  let content: string;
  if (product.usesNativeSDK && !order.credentialsPresent) {
    content = m.subscription_confirmed_refresh_message({ displayName: product.displayName, product: product.name });
    actions = [
      {
        text: m.refresh_button(),
        action: async (a) => {
          // TODO: consider whether this should call `recoverOrder` directly
          await goto(`${resolve('/account/')}?intent=recover&product=${product.name}`);
          a.dismiss();
        },
      },
    ];
  } else if (product.usesNativeSDK) {
    content = m.fond_loud_magpie_cheer({ product: product.name, displayName: product.displayName });
  } else {
    content = m.verifying_access_message({ displayName: product.displayName });
    actions = [
      {
        text: m.return_to_product_button({ displayName: product.displayName }),
        action: () => (location.href = getRecoveryURL(productDomain, orderId)),
      },
    ];
  }

  showAlert(
    {
      content,
      type: "success",
      actions,
    },
    ALERT_DURATION
  );
};

export const ALREADY_SUBSCRIBED = (order: Order) => {
  const { product, productDomain, orderId } = order;

  let actions: Array<any> = [];
  let content: string = m.wide_great_finch_hum({ displayName: product.displayName, product: product.name });
  if (product.usesNativeSDK && !order.credentialsPresent) {
    actions = [
      {
        text: m.refresh_button(),
        component: AwaitButton,
        action: async (a) => {
          await recoverOrder(order);
          a.dismiss();
        },
      },
    ];

    if (product.name === "origin") {
      actions = [
        ...actions,
        {
          text: m.copy_order_id(),
          textToCopy: orderId,
          component: CopyText,
        },
      ];
    }
  } else if (!product.usesNativeSDK) {
    actions = [
      {
        text: m.return_to_product_button({ displayName: product.displayName }),
        action: () => (location.href = getRecoveryURL(productDomain, orderId)),
      },
    ];
  }

  showAlert({
    content,
    type: "success",
    actions,
  });
};

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export const showAlert = (
  options: PartialBy<AlertOptions, "actions">,
  duration: number = null,
  dismissible: boolean = true
) => {
  let actions = options.actions ?? [];
  return _showAlert({ ...options, actions }, duration, dismissible);
};

export const showError = (
  options: Omit<PartialBy<AlertOptions, "actions" | "content">, "type" | "component"> & { error?: any },
  duration = 0
) => {
  showAlert({ content: options.error?.message, ...options, type: "error", component: ErrorAlert }, duration);
  options.error && console.error(options.error);
};
